import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { IGNORE_PUBLIC_URLS, PUBLIC_URLS, STORAGE_KEYS } from '../constants';
import { refreshTokenApi } from '../services';
import { MessageService } from './message.service';
import { checkDomainIsAdmin } from '../helpers/checkDomainIfAdmin';

const axiosService = () => {
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  // Create instance
  const instance = axios.create(defaultOptions);

  // Set the AUTH token for any request
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      //if the url is in the publicUrls array, don't add the authorization header
      if (
        PUBLIC_URLS?.some((urlStr) => config?.url?.includes(urlStr)) &&
        !IGNORE_PUBLIC_URLS?.some((urlStr) => config?.url?.includes(urlStr))
      ) {
        return config;
      }
      let token;
      if (checkDomainIsAdmin()) {
        token = localStorage.getItem(STORAGE_KEYS.ADM_ACCESS_TOKEN);
      } else {
        token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      }
      if (token) {
        config.headers = {
          Authorization: `Bearer ${token}`,
        } as InternalAxiosRequestConfig['headers'];
      }

      return config;
    }
  );
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      // Any status code that lie within the range of 2xx cause this function to trigger
      // Do something with response data
      return response;
    },
    async (error) => {
      // The request was made and the server responded with a status code 401 (Unauthorized)
      if (error?.response?.status === 401) {
        let refreshToken;

        // Get the refresh token from the localstorage
        if (checkDomainIsAdmin()) {
          refreshToken = localStorage.getItem(STORAGE_KEYS.ADM_REFRESH_TOKEN);
        } else {
          refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
        }

        // If there is no refresh token, log the user out
        if (!refreshToken) {
          MessageService.requestLogout();
          return Promise.reject(error?.response);
        }

        try {
          // Make a request to the server to get a new access token using the refresh token
          const { data: tokens } = await refreshTokenApi(refreshToken);

          if (!tokens?.accessToken) {
            MessageService.requestLogout();
          }

          // If the request is successful, set the new tokens
          MessageService.requestRefreshTokens(tokens);

          // Retry the original request with the new access token
          const config = error.config;
          config.headers.Authorization = `Bearer ${tokens.accessToken}`;

          return axios.request(config);
        } catch (e) {
          // If the request for a new access token fails, log the user out
          MessageService.requestLogout();
          return Promise.reject(error?.response);
        }
      } else if (
        error?.response?.status > 500 &&
        error?.response?.status < 600
      ) {
        // TODO: handle server error logging
        console.error('Server error:', error?.response);
        return;
      }

      console.error('Server error:', error?.response);
      return Promise.reject(error?.response);
    }
  );
  return instance;
};
export const httpService = axiosService();
